import pluralize from "pluralize";
import { type VFC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch } from "react-router";

import { ActionButton, LoadingSpinner } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import {
  type IRsqlComparisonExpr,
  type IRsqlFilterTree,
  type RsqlFilterConstraint,
  parseFilterTree,
  toComparisonArray,
} from "../../app-utils/rsql.js";
import { type AdvancedSearchParams, type Group } from "../../graphql.js";
import { populateAdvancedSearchPanel } from "../../redux/modules/advanced-search.js";
import { showNavbarElement } from "../../redux/modules/navbar.js";
import { type SavedViewState, loadView } from "../../redux/modules/saved-view.js";
import { type ISearchResultState, shouldLoadSearchResult } from "../../redux/modules/search-results.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { ContentPlaceholder } from "./ContentPlaceholder.js";
import { ContextWidgetType, type INavigationContextProps } from "./navigation-panel/ContextWidget.js";
import GroupPageLayout from "./navigation-panel/GroupPageLayout.js";
import { type INavPanelCategory } from "./navigation-panel/NavigationPanel.js";
import PageLayout from "./navigation-panel/PageLayout.js";

const getAdvancedSearchColumns = (searchResults: ISearchResultState): string[] => {
  const searchParams: AdvancedSearchParams = searchResults.advancedSearchParams;
  const filter: IRsqlFilterTree =
    searchParams.attributesQueryExpression && parseFilterTree(searchParams.attributesQueryExpression);
  const constraints: RsqlFilterConstraint[] = toComparisonArray(filter);
  // we can assume that the array contains only single filter expressions, and the key of each corresponds to column field name
  return constraints.map((f: RsqlFilterConstraint) => (f as IRsqlComparisonExpr).key);
};

const getSearchResultPageNavPanelCategory = (
  searchResults: ISearchResultState,
  openAdvancedSearchPanel: (params: AdvancedSearchParams) => void,
  absolutePath = false,
): INavPanelCategory => {
  const { advancedSearchParams } = searchResults;
  return {
    label: advancedSearchParams ? "Advanced Search Results" : "Search Results",
    widget: advancedSearchParams ? (
      <ActionButton
        label={"Edit Search Criteria"}
        onPress={() => openAdvancedSearchPanel(advancedSearchParams)}
        icon={"search"}
      />
    ) : null,
    navItems: searchResults.groups.map((group: Group) => ({
      label: pluralize.plural(group.typeDisplayName),
      badge: `${group.itemCount}`,
      route: `${absolutePath ? "/search/" + searchResults.id + "/" : ""}${group.type}${
        group.itemSubTypeId ? "/" + group.itemSubTypeId : ""
      }`,
      pageComponent: (
        <GroupPageLayout
          group={group}
          tableInitializationOptions={
            searchResults.advancedSearchParams && { visibleColumns: getAdvancedSearchColumns(searchResults) }
          }
          documentTitleContext={
            advancedSearchParams ? "Advanced Search Results" : `${searchResults.name} [Search Results]`
          }
        />
      ),
    })),
  };
};

const SearchResultPage: VFC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const match = useMatch("/search/:searchResultId/*");
  const searchResults = useSelector<IAppState, ISearchResultState>((state) => state.searchResults);
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadSearchResult, match?.params.searchResultId);

  useEffect(() => {
    void load()?.then((result) => {
      if (viewId) {
        void dispatch(loadView(viewId));
        // Afterwards only loadTableRows should run, but not loadTable
        //  since the table configuration was restored from the view by the table data reducer.
        // Loading of the table rows is triggered by the grid on view port change.
      }
      return result;
    });
  }, [dispatch, load, viewId]);

  const getContextProps = (): INavigationContextProps => {
    if (viewId && !isSavedViewLoading && view && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    }

    return null;
  };

  const noResults = searchResults.groups.length === 0;

  return searchResults.isLoading ? (
    <div className={styleLayout.page}>
      <LoadingSpinner inCenter />
    </div>
  ) : noResults ? (
    <div className={styleLayout.page}>
      <ContentPlaceholder text={"No results"} />
    </div>
  ) : (
    <PageLayout
      contextWidgetProps={getContextProps()}
      navPanelCategories={[
        getSearchResultPageNavPanelCategory(searchResults, (params) => {
          dispatch(populateAdvancedSearchPanel(params));
          dispatch(showNavbarElement({ showAdvancedSearchPanel: true }));
        }),
      ]}
    />
  );
};

export { getSearchResultPageNavPanelCategory, SearchResultPage };
